import React from "react";
import PropTypes from "prop-types";
import { ResponsiveTreeMap } from '@nivo/treemap';

// --- UTILS ---


// Format large numbers (market cap)
function formatMarketCap(n) {
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n}`;
}

// Normalize market caps (log scale, sum to 1)
function normalizeMarketCaps(cryptos) {
  // Ignore 0 or negative market caps
  const logs = cryptos.map(c => ({ ...c, logCap: c.marketCap > 0 ? Math.log10(c.marketCap) : 0 }));
  const total = logs.reduce((sum, c) => sum + c.logCap, 0) || 1;
  return logs.map(c => ({ ...c, weight: c.logCap / total }));
}

// --- MAIN COMPONENT ---

function CryptoHeatmap({ cryptos, topN = 30, sortBy = 'marketCap', style = {} }) {
  // Filter and sort top N by market cap
  // Debug temporaire : log les cryptos reçues
  React.useEffect(() => {
    if (cryptos && cryptos.length > 0) {
      // Affiche les valeurs de priceChange24h et priceChangePercentage24h
      // eslint-disable-next-line
      console.log('cryptos for heatmap', cryptos.map(c => ({symbol: c.symbol, marketCap: c.marketCap, priceChange24h: c.priceChange24h, priceChangePercentage24h: c.priceChangePercentage24h})));
    }
  }, [cryptos]);

  const filtered = React.useMemo(() => {
    let sorted = [...cryptos];
    if (sortBy === 'marketCap') {
      sorted = sorted.filter(c => c.marketCap > 0).sort((a, b) => b.marketCap - a.marketCap);
    } else if (sortBy === 'variation') {
      sorted = sorted.sort((a, b) => Math.abs(b.priceChange24h) - Math.abs(a.priceChange24h));
    }
    return sorted.slice(0, topN);
  }, [cryptos, topN, sortBy]);

  const tiles = React.useMemo(() => normalizeMarketCaps(filtered), [filtered]);

  // Structure des données pour Nivo Treemap
  const nivoData = React.useMemo(() => ({
    name: 'cryptos',
    children: tiles.map(c => {
      let change = typeof c.priceChangePercentage24h === 'number' && !isNaN(c.priceChangePercentage24h)
        ? c.priceChangePercentage24h
        : (typeof c.priceChange24h === 'number' ? c.priceChange24h : 0);
      return {
        id: c.symbol,
        name: c.symbol,
        value: c.weight > 0 ? c.weight : 0.01,
        marketCap: c.marketCap,
        priceChange24h: change,
      };
    })
  }), [tiles]);

  // Empty state
  if (!cryptos || cryptos.length === 0) {
    return (
      <div style={{ width: "100%", minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: 20, ...style }}>
        Aucune donnée à afficher
      </div>
    );
  }

  // Palette de couleurs selon variation
  const getColor = node => {
    const v = node.data.priceChange24h;
    if (v >= 7) return '#006400'; // dark green
    if (v >= 2) return '#90EE90'; // light green
    if (v <= -7) return '#FF6347'; // light red
    if (v <= -2) return '#D3D3D3'; // neutral gray
    return '#222'; // default
  };

  return (
    <div style={{ width: "100%", minHeight: 320, background: "#222", borderRadius: 12, padding: 8, ...style }}>
      <ResponsiveTreeMap
        data={nivoData}
        identity="id"
        value="value"
        colors={getColor}
        label={node => `${node.data.name}`}
        labelSkipSize={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
        borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
        nodeOpacity={1}
        nodeBorderWidth={3}
        nodeBorderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
        tooltip={({ node }) => (
          <div style={{ padding: 8, background: '#222', color: '#fff', borderRadius: 8 }}>
            <b>{node.data.name}</b><br />
            Market cap: <b>{formatMarketCap(node.data.marketCap)}</b><br />
            Variation 24h: <span style={{ color: getColor(node) }}>{node.data.priceChange24h > 0 ? '+' : ''}{node.data.priceChange24h?.toFixed(2)}%</span>
          </div>
        )}
        theme={{
          fontSize: 16,
          tooltip: { container: { background: '#222', color: '#fff', borderRadius: 8 } }
        }}
        animate={true}
        motionConfig="wobbly"
        leavesOnly={true}
        height={600}
        width={window.innerWidth * 0.95}
        style={{ width: "100%", height: "min(60vw,600px)", minHeight: 320, borderRadius: 12, background: "#222" }}
      />
    </div>
  );
}

CryptoHeatmap.propTypes = {
  cryptos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    symbol: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    marketCap: PropTypes.number.isRequired,
    priceChange24h: PropTypes.number.isRequired
  })).isRequired,
  topN: PropTypes.number,
  sortBy: PropTypes.string,
  style: PropTypes.object
};

export default CryptoHeatmap;
